import {useEffect, useRef, useState} from 'react';
import {Form, notification} from 'antd';
import {useHistory, useLocation} from 'react-router-dom';
import {getCurrentUser} from 'aws-amplify/auth';
import type {PlacementStatus} from '@PikaDoApp/npm-pikado-logic/dist/modules/enrollment/graphql/enrollment.interface';
import {
  Level,
  Package,
  Status,
  StudentsConnection,
  Test,
} from '../../../graphql/API';
import {StudentsService} from '../../Students';
import {PackagesService} from '../../Package';
import {LevelsService} from '../../Level';
import {PlacementTestsService} from '../../PlacementTest/PlacementTestsService';
import {EnrollmentsService} from '../EnrollmentsService';
import {CurriculumsService} from '../../Curriculums/CurriculumsService';

const studentsService = new StudentsService();
const packagesService = new PackagesService();
const levelService = new LevelsService();
const placementTestsService = new PlacementTestsService();
const enrollmentsService = new EnrollmentsService();
const curriculumsService = new CurriculumsService();

export type EnrollmentFormValues = {
  studentId: string;
  packageId: string;
  levelId: string;
  testId?: string;
  placementStatus: PlacementStatus;
  paymentStatus: Status;
  startDate: any;
  selfRating: number;
  paymentMethod: string;
  finalPrice?: number;
  currency: string;
  curriculumId?: string;
  useCustomPrice: boolean;
};

export const useEnrollmentForm = () => {
  const [form] = Form.useForm<EnrollmentFormValues>();
  const history = useHistory();
  const location = useLocation<{studentId?: string; studentName?: string}>();

  const preSelectedStudent = location.state?.studentId
    ? {
        id: location.state.studentId,
        name: location.state.studentName || 'Unknown',
      }
    : null;

  const [students, setStudents] = useState<StudentsConnection>();
  const [packages, setPackages] = useState<Package[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [tests, setTests] = useState<Test[]>([]);
  const [curriculums, setCurriculums] = useState<any[]>([]);

  const [isLoadingStudents, setIsLoadingStudents] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchBy, setSearchBy] = useState<'NAME' | 'ID'>('NAME');
  const [showCustomPrice, setShowCustomPrice] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const searchTimeout = useRef<any>();

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [currs, lvls, pkgs, stds, testsResult] = await Promise.allSettled(
          [
            curriculumsService.getCurriculums(),
            levelService.getLevels(),
            packagesService.getPackages(1, 100),
            studentsService.getStudents(null),
            placementTestsService.getPlacementTests(1, 100),
          ],
        );

        if (currs.status === 'fulfilled') setCurriculums(currs.value || []);
        if (lvls.status === 'fulfilled') setLevels(lvls.value);
        if (pkgs.status === 'fulfilled') {
          const exercisesPackages = (pkgs.value.items as any[]).filter(
            p => p.onlyAI,
          );
          setPackages(exercisesPackages as any);
        }
        if (stds.status === 'fulfilled') {
          setStudents(stds.value);
          setIsLoadingStudents(false);
        }
        if (testsResult.status === 'fulfilled')
          setTests(testsResult.value.items);
      } catch (err) {
        notification.error({
          message: 'Failed to load data',
          description: 'Please refresh and try again.',
        });
      }
    };

    fetchAll();
  }, []);

  // Pre-select student if navigated from student page
  useEffect(() => {
    if (preSelectedStudent) {
      form.setFieldValue('studentId', preSelectedStudent.id);
      setCurrentStep(1);
    }
  }, [preSelectedStudent?.id]);

  const searchStudents = (nextToken: string | null, textSearch?: string) => {
    setIsLoadingStudents(true);
    studentsService
      .getStudents(nextToken, searchBy, textSearch)
      .then(stds => {
        setStudents(stds);
        setIsLoadingStudents(false);
      })
      .catch(() => setIsLoadingStudents(false));
  };

  const onStudentSearch = (value: string) => {
    setIsLoadingStudents(true);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchStudents(null, value), 600);
  };

  const onPopupScroll = (event: any) => {
    const target = event.target;
    if (
      !isLoadingStudents &&
      target.scrollTop + target.offsetHeight === target.scrollHeight
    ) {
      setIsLoadingStudents(true);
      studentsService
        .getStudents(students?.nextToken ?? null)
        .then(stds => {
          setStudents(prev => ({
            ...prev,
            ...stds,
            items: [...(prev?.items ?? []), ...stds.items],
          }));
          setIsLoadingStudents(false);
        })
        .catch(() => setIsLoadingStudents(false));
    }
  };

  const onTestSearch = (value: string) => {
    if (value.length > 3) {
      placementTestsService
        .searchPlacementTestsByName(value)
        .then(r => setTests(r.items));
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const studentEnrollments =
        await enrollmentsService.getEnrollmentsByStudentId(values.studentId);
      console.log('studentEnrollments', studentEnrollments);
      const latestEnrollment = studentEnrollments?.length
        ? studentEnrollments.reduce((a: any, b: any) =>
            a.updatedAt > b.updatedAt ? a : b,
          )
        : null;

      if ((latestEnrollment as any)?.packageId?.packageDuration >= 90) {
        notification.warning({
          message: 'Enrollment Blocked',
          description:
            'Student already has an active enrollment this year that must be completed first.',
          duration: 6,
        });
        setIsSubmitting(false);
        return;
      }

      let createdBy = 'unknown';
      try {
        const user = await getCurrentUser();
        createdBy = user.signInDetails?.loginId || user.userId || 'unknown';
      } catch {}

      const input: any = {
        studentId: values.studentId,
        packageId: values.packageId,
        placementStatus: values.placementStatus || 'TODO',
        createdBy,
        startDate: values.startDate ? values.startDate.toString() : null,
        levelId: values.levelId || null,
        placementTestId: values.testId || null,
        curriculumId: values.curriculumId || null,
        paymentMethod: values.paymentMethod as any,
        finalPrice: {
          currency: values.currency || 'EGP',
          ...(showCustomPrice && values.finalPrice !== undefined
            ? {price: values.finalPrice}
            : {}),
        },
      };

      await enrollmentsService.createEnrollment(input);

      notification.success({
        message: 'Enrollment Created!',
        description: 'The student has been successfully enrolled.',
        duration: 4,
      });

      setTimeout(() => history.push('/enrollments'), 1500);
    } catch (err: any) {
      if (err?.errorFields) return; // antd validation error, already shown inline
      notification.error({
        message: 'Failed to Create Enrollment',
        description: err?.message || 'An unexpected error occurred.',
        duration: 6,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    students,
    packages,
    levels,
    tests,
    curriculums,
    isLoadingStudents,
    isSubmitting,
    searchBy,
    setSearchBy,
    showCustomPrice,
    setShowCustomPrice,
    currentStep,
    setCurrentStep,
    searchStudents,
    onStudentSearch,
    onPopupScroll,
    onTestSearch,
    onSubmit,
    history,
    preSelectedStudent,
  };
};
